import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const CATEGORY_ICONS = { cruise: "🚢", resort: "🏨", "short-notice": "⚡", meetup: "🎰" };

function EventCard({ event, ctaText = "View Details →" }) {
  const coverImage = event.imageUrl || event.images?.[0]?.url;
  const photoCount = event.images?.length || 0;
  const linkCount = event.links?.length || 0;

  // Capacity urgency
  const capacity = event.maxCapacity || 0;
  const booked = event.totalHeadcount || event.currentInquiries || 0;
  const spotsLeft = capacity > 0 ? capacity - booked : null;
  const capacityPct = capacity > 0 ? (booked / capacity) * 100 : 0;
  const isLowCapacity = spotsLeft !== null && spotsLeft <= Math.max(5, capacity * 0.15);
  const isAlmostFull = spotsLeft !== null && spotsLeft <= 3;

  // Countdown — only show if event starts within 14 days
  const daysUntil = event.startDate ? Math.ceil((new Date(event.startDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const showCountdown = daysUntil !== null && daysUntil > 0 && daysUntil <= 14;
  const isUrgent = daysUntil !== null && daysUntil <= 3 && daysUntil > 0;
  const isExpired = event.status === "completed" || (daysUntil !== null && daysUntil < 0);

  return (
    <Link to={`/events/${event.eventId}`} style={{ textDecoration: "none", color: "inherit" }}>
      <article className="event-card" style={isExpired ? { opacity: 0.6 } : {}}>
        <div className={`event-card-image ${coverImage ? "has-photo" : ""}`}>
          {coverImage ? (
            <img src={coverImage} alt={event.title} className="event-card-photo" />
          ) : (
            <span className="event-card-emoji">
              {CATEGORY_ICONS[event.eventType] || "🎲"}
            </span>
          )}
          <span className="event-type event-type-badge">{event.eventType}</span>

          {/* Urgency badges */}
          <div className="event-card-badges">
            {isExpired && <span className="urgency-badge" style={{ background: "var(--gray-600)", color: "white" }}>Event Ended</span>}
            {!isExpired && isAlmostFull && <span className="urgency-badge urgency-critical">🔥 Almost Full</span>}
            {!isExpired && isLowCapacity && !isAlmostFull && <span className="urgency-badge urgency-warning">⚡ Filling Fast</span>}
            {!isExpired && isUrgent && <span className="urgency-badge urgency-critical">⏰ {daysUntil}d left</span>}
            {!isExpired && showCountdown && !isUrgent && <span className="urgency-badge urgency-info">📅 {daysUntil} days left</span>}
          </div>

          {(photoCount > 1 || linkCount > 0) && (
            <div className="event-card-indicators">
              {photoCount > 1 && <span className="event-card-indicator">📷 {photoCount}</span>}
              {linkCount > 0 && <span className="event-card-indicator">🔗 {linkCount}</span>}
            </div>
          )}
        </div>
        <div className="event-card-body">
          <h3>{event.title}</h3>
          <p>{event.description?.substring(0, 110)}...</p>
          <div className="event-meta">
            <span>📍 {event.location}</span>
            <span>📅 {event.startDate}</span>
          </div>

          {/* Capacity bar */}
          {capacity > 0 && (
            <div className="capacity-bar-wrap">
              <div className="capacity-bar">
                <div className="capacity-fill" style={{ width: `${Math.min(capacityPct, 100)}%`, background: capacityPct >= 85 ? "#EF4444" : capacityPct >= 60 ? "#F59E0B" : "#10B981" }} />
              </div>
              <span className="capacity-text">
                {spotsLeft > 0 ? `${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} left` : "Full"}
              </span>
            </div>
          )}

          <div className="event-card-cta">{isExpired ? "View Past Event" : ctaText}</div>
        </div>
      </article>
    </Link>
  );
}

export default EventCard;
